import { useMemo } from "react";
import { useSiteSettings } from "@/hooks/use-site-settings";
import { useLanguage } from "@/context/LanguageContext";
import {
  RotateCcw,
  Truck,
  Clock,
  Sparkles,
  Trophy,
  Shield,
  BadgeCheck,
  CreditCard,
  Package,
  Headphones,
  Gift,
  Star,
  Heart,
  type LucideIcon,
} from "lucide-react";

export const TOP_BAR_ICONS: Record<string, LucideIcon> = {
  RotateCcw,
  Truck,
  Clock,
  Sparkles,
  Trophy,
  Shield,
  BadgeCheck,
  CreditCard,
  Package,
  Headphones,
  Gift,
  Star,
  Heart,
};

export type TopBarItem = {
  icon: string;
  text_lv: string;
  text_en: string;
};

export const DEFAULT_TOP_BAR_ITEMS: TopBarItem[] = [
  { icon: "RotateCcw", text_lv: "14 DIENU ATGRIEŠANA", text_en: "14 DAYS RETURN" },
  { icon: "Truck", text_lv: "BEZMAKSAS PIEGĀDE NO 150 EUR", text_en: "FREE SHIPPING FROM 150 EUR" },
  { icon: "Trophy", text_lv: "LABĀKIE PRODUKTI PAR LABĀKAJĀM CENĀM", text_en: "BEST PRODUCTS FOR BEST PRICES" },
];

export function parseTopBarItems(raw: string | undefined): TopBarItem[] {
  if (!raw) return DEFAULT_TOP_BAR_ITEMS;
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) return parsed as TopBarItem[];
  } catch {
    /* ignore */
  }
  return DEFAULT_TOP_BAR_ITEMS;
}

const TopBar = () => {
  const { data: settings } = useSiteSettings();
  const { lang } = useLanguage();

  const enabled = (settings?.top_bar_enabled ?? "true") === "true";
  const bg = settings?.top_bar_bg || "#574331";
  const fg = settings?.top_bar_fg || "#ffffff";
  const mobileMode = (settings?.top_bar_mobile_mode || "marquee") as "marquee" | "stack";
  const items = useMemo(() => parseTopBarItems(settings?.top_bar_items), [settings?.top_bar_items]);

  if (!enabled || items.length === 0) return null;

  const getText = (it: TopBarItem) => (lang === "lv" ? it.text_lv : it.text_en) || it.text_en || it.text_lv;

  const renderItem = (it: TopBarItem, key: string | number) => {
    const Icon = TOP_BAR_ICONS[it.icon] || Sparkles;
    return (
      <div key={key} className="flex items-center gap-2 whitespace-nowrap">
        <Icon size={14} className="shrink-0 opacity-90" strokeWidth={2.25} />
        <span className="text-[11px] md:text-[12px] font-medium tracking-[0.08em] uppercase">
          {getText(it)}
        </span>
      </div>
    );
  };

  return (
    <div
      className="w-full border-b border-white/10"
      style={{ backgroundColor: bg, color: fg }}
      role="region"
      aria-label="Store announcements"
    >
      {/* Desktop: evenly distributed row */}
      <div className="hidden md:block max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between py-2 gap-4">
          {items.map((it, i) => (
            <div key={i} className="flex items-center gap-4 flex-1 justify-center">
              {renderItem(it, i)}
              {i < items.length - 1 && (
                <span aria-hidden className="hidden lg:inline-block h-3 w-px bg-[#574331]" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Mobile */}
      <div className="md:hidden">
        {mobileMode === "stack" ? (
          <div className="px-3 py-2 grid grid-cols-2 gap-x-3 gap-y-1.5">
            {items.map((it, i) => renderItem(it, i))}
          </div>
        ) : (
          <div className="topbar-marquee py-2 overflow-hidden">
            <div className="topbar-marquee-track">
              {[...items, ...items].map((it, i) => (
                <div key={i} className="px-5 flex items-center">
                  {renderItem(it, i)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TopBar;
